import moment from 'moment'

import { arrayToMap } from 'lib/arrayUtils'
import { BedbankForm } from 'components/Booking/Bedbank/Payment/types'
import { FlightsFareTypes } from 'constants/flight'
import { isCoverGeniusEnabled } from 'lib/insuranceHelpers'
import { ISO_DATE_FORMAT } from 'constants/dateFormats'
import { JOURNEY_TYPE_V1, parseContactNumber } from 'lib/flights'
import {
  OFFER_DYNAMIC_PRICING_TYPES,
  OFFER_TYPE_HOTEL,
  OFFER_TYPE_TOUR,
} from 'constants/offer'
import {
  RESERVATION_TYPE_BOOK_LATER,
  RESERVATION_TYPE_INSTANT_BOOKING,
} from 'constants/reservation'
import {
  ITEM_TYPE_ADDON,
  ITEM_TYPE_BEDBANK,
  ITEM_TYPE_BNBL_EXPERIENCE,
  ITEM_TYPE_EXPERIENCE,
  ITEM_TYPE_GIFT_CARD,
  ITEM_TYPE_INSURANCE,
} from 'constants/cart'
import config from 'constants/config'
import generateOccupancyStringByRoom from 'lib/generateOccupancyStringByRoom'

function parseCoverGeniusCartInsuranceItem(insurance: App.CartInsurance, brand: string) {
  return {
    quote_id: insurance.insuranceQuote.id,
    brand,
    product_id: insurance.insuranceQuote.productId,
    transaction_key: insurance.transactionKey,
    policy_holder_firstname: insurance.insuranceTravellers[0].givenName,
    policy_holder_surname: insurance.insuranceTravellers[0].surname,
    travellers_details: insurance.insuranceTravellers.map(t => ({
      firstname: t.givenName,
      surname: t.surname,
    })),
    type: ITEM_TYPE_INSURANCE,
  }
}

function parseCartBedbankPhonePrefix(prefix: string): string {
  return '+' + prefix.replace(/^\++/g, '')
}

// parseCartInsuranceItem parses the insurance object in the cart
export function parseCartInsuranceItem(
  insurance: App.CartInsurance,
  currentRegionCode: string,
) {
  if (isCoverGeniusEnabled(currentRegionCode)) {
    return parseCoverGeniusCartInsuranceItem(insurance, config.BRAND)
  }
}

export const baggageMap = (baggage: Record<string, Array<App.AddedBaggage>>, journey: App.Journey) => {
  const mappedBaggage = []

  const getBaggageCategory = id => {
    const found = mappedBaggage.find(b => b.id === id)
    if (found) return found

    const created = {
      id,
      departing: { count: 0 },
      returning: { count: 0 },
    }
    mappedBaggage.push(created)
    return created
  }

  const departingBaggage = baggage[journey.departing.journeyKey] || baggage[journey.id]?.[0] && [baggage[journey.id][0]] || []
  const returningBaggage = baggage[journey.returning?.journeyKey] || baggage[journey.id]?.[1] && [baggage[journey.id][1]] || []

  departingBaggage.forEach(b => {
    if (b.count > 0) {
      const baggageCategory = getBaggageCategory(b.id)
      baggageCategory.departing.count = b.count
    }
  })
  returningBaggage.forEach(b => {
    if (b.count > 0) {
      const baggageCategory = getBaggageCategory(b.id)
      baggageCategory.returning.count = b.count
    }
  })

  return mappedBaggage
}

function passengerMap(
  passenger: App.Passenger,
  baggage: Record<string, Array<App.AddedBaggage>>,
  journey: App.Journey,
) {
  const qfLoyaltyProgramNumbers = passenger.qfLoyaltyProgramNumbers
  const vaLoyaltyProgramNumbers = passenger.vaLoyaltyProgramNumbers
  const frequentFlyer: App.LoyaltyProgramNumbers['frequentFlyer'] = []

  if (qfLoyaltyProgramNumbers) {
    frequentFlyer.push({
      number: qfLoyaltyProgramNumbers,
      programCarrier: 'QF',
      bookingCarrier: 'QF',
    })
  }

  if (vaLoyaltyProgramNumbers) {
    frequentFlyer.push({
      number: vaLoyaltyProgramNumbers,
      programCarrier: 'VA',
      bookingCarrier: 'VA',
    })
  }

  const serverPassenger: any = {
    type: passenger.type,
    birth_date: passenger.birthDate,
    given_name: passenger.firstName,
    surname: passenger.lastName,
    title: passenger.title.toLowerCase(),
    passport_number: passenger.passportNumber,
    country_of_issue: passenger.countryOfIssue,
    nationality: passenger.nationality,
    gender: passenger.gender,
    // server doesn't accept empty strings for dates, needs undefined/null
    passport_expiry_date: passenger.passportExpiryDate ? passenger.passportExpiryDate : undefined,
    state_of_residence: passenger.stateOfResidence,
    extras: {},
    loyalty_program_numbers: frequentFlyer.length > 0 ? {
      frequent_flyer: frequentFlyer.map(ff => ({
        number: ff.number,
        program_carrier: ff.programCarrier,
        booking_carrier: ff.bookingCarrier,
      })),
    } : undefined,
  }

  if (passenger.middleName) {
    serverPassenger.middle_names = [passenger.middleName]
  }

  if (baggage) {
    serverPassenger.extras.baggage = baggageMap(baggage, journey)
  }

  if (passenger.frequentFlyerNumber && passenger.frequentFlyerProgram) {
    serverPassenger.frequent_flyer_number = {
      type: passenger.frequentFlyerProgram,
      number: passenger.frequentFlyerNumber,
    }
  }

  const { email, phone, postCode = '' } = passenger
  if (config.AU_CONTACT_TRACING_ENABLED && email && phone) {
    serverPassenger.email = email
    serverPassenger.phone = parseContactNumber(phone)
    serverPassenger.post_code = postCode
  }

  return serverPassenger
}

export function parseCartFlightItem(
  passengers: Array<App.Passenger>,
  flight: App.CartFlight,
  baggageCost: number,
  offerType: App.OfferType,
  bookDatesOnly?: boolean,
) {
  const primaryContact = passengers.find(passenger => passenger.isPrimary)
  const { itemType, fareType, departing, returning } = flight.journey

  const journeyData = {
    id: flight.journey.id,
    cost: flight.journey.cost + baggageCost,
    tax: flight.journey.tax,
    searchId: flight.journey.searchId,
    ...(itemType === JOURNEY_TYPE_V1 && fareType === FlightsFareTypes.RETURN && {
      selectedFareIds: [departing.journeyKey, returning?.journeyKey],
    }),
  }
  const offer_type: Array<string> = [offerType]

  if (bookDatesOnly) {
    offer_type.push(RESERVATION_TYPE_BOOK_LATER)
  }

  const flightPassengerMap = arrayToMap(flight.passengers, p => p.id)
  return {
    provider: flight.journey.provider,
    transaction_key: flight.transactionKey,
    type: 'flight',
    offer_type,
    contact_email_address: primaryContact.email,
    contact_phone_number: parseContactNumber(primaryContact.phone),
    passengers: passengers.map(formPassenger => {
      const flightPassenger = flightPassengerMap.get(formPassenger.id)
      return passengerMap(formPassenger, flightPassenger.addedExtras.baggage, flight.journey)
    }),
    journey: journeyData,
  }
}

export function parseCartGiftCardItem(
  cartGiftCard: App.CartGiftCard,
  account?: App.AuthAccount,
) {
  return {
    type: ITEM_TYPE_GIFT_CARD,
    transaction_key: cartGiftCard.transactionKey,
    gift_card_value: cartGiftCard.price,
    personalised: cartGiftCard.personalisation,
    customer_email: account && account.email,
    customer_name: account && account.fullName,
    brand: config.BRAND,
  }
}

/**
 * Parses the Bedbank data so that it can be included in an order.
 */
export function parseCartBedbankItem(
  item: App.CartBedbank,
  formData?: BedbankForm,
) {
  const data = {
    type: ITEM_TYPE_BEDBANK,
    transaction_key: item.transactionKey,
    session_id: item.sessionId,
    bed_group_id: item.bedGroupId,
    booking_email: item.bookingEmail,
    booking_phone: `${item.bookingPhonePrefix}${item.bookingPhone}`,
    rooms: item.rooms.map(room => ({
      occupancy: generateOccupancyStringByRoom(room.occupancy),
      guest_first_name: room.guestFirstName,
      guest_last_name: room.guestLastName,
      guest_special_requests: room.guestSpecialRequests,
    })),
    brand: config.BRAND,
  }

  if (formData) {
    const prefix = parseCartBedbankPhonePrefix(formData.phone.prefix)
    data.bed_group_id = formData.bedGroupId
    data.booking_email = formData.bookingEmail
    data.booking_phone = `${prefix}${formData.phone.phone}`
    data.rooms = data.rooms.map((room, index) => {
      const bedbankGuest = formData.guests[index + 1]
      return {
        ...room,
        guest_first_name: bedbankGuest.guestFirstName,
        guest_last_name: bedbankGuest.guestLastName,
        guest_special_requests: bedbankGuest.guestSpecialRequests,
      }
    })
  }

  return data
}

export function parseCartAddonItem(
  addon: App.PackageAddonItem,
  transactionKey: string,
  offer: App.Offer,
  pkg: App.Package,
  account?: App.AuthAccount,
  parentAccommodationItemId?: string,
) {
  const data: any = {
    type: ITEM_TYPE_ADDON,
    transaction_key: transactionKey,
    addon_id: addon.idSalesforceExternal,
    complimentary: addon.complimentary,
    customer_email: account && account.email,
    customer_name: account && account.fullName,
    brand: 'luxuryescapes',
  }

  if (offer && pkg) {
    data.offer_id = offer.id
    data.package_id = pkg.id
  }
  if (parentAccommodationItemId) {
    data.accommodation_parent_item_id = parentAccommodationItemId
  }
  return data
}

export function parseCartOfferItem(
  item: App.CartItem,
  occupancy: App.Occupants,
  cartAccommodation: App.CartAccommodation,
  account?: App.AuthAccount,
  primaryPassenger?: App.Passenger,
  passengerDetailsWhenFlightsIncluded?: App.Passenger,
  cartItemTotal?: number,
) {
  const { package: pkg, offer, reservationType, startDate, endDate } = cartAccommodation
  const data: any = {
    cart_item_id: item.id,
    offer_id: offer.id,
    package_id: pkg.id,
    room_rate_id: pkg.roomRate?.id,
    transaction_key: item.transactionKey,
    offer_booking_type: 'reservation',
    offer_type: offer.type,
    offer_name: offer.name,
    package_name: pkg.name,
    offer_slug: offer.slug,
    value: item.value,
    price: item.price,
    surcharge: item.reservation?.durationSurchargeTotal ?? 0,
    extra_guest_surcharge: item.reservation?.extraGuestSurcharge ?? 0,
    property_fees: item.reservation?.propertyFees ?? 0,
    total: cartItemTotal,
  }

  data.localizedPrices = pkg.trackingPrice

  if (account) {
    data.customer_email = account.email
    data.customer_name = account.fullName
  }

  switch (offer.parentType) {
    case OFFER_TYPE_HOTEL:
      data.number_of_nights = pkg.duration
      if (item.reservation) {
        data.reservation_type = reservationType

        if (reservationType === RESERVATION_TYPE_INSTANT_BOOKING) {
          data.check_in = moment.utc(startDate).format(ISO_DATE_FORMAT)
          data.check_out = moment.utc(endDate).format(ISO_DATE_FORMAT)
          data.number_of_adults = occupancy.adults
          data.number_of_children = occupancy.children
          data.number_of_infants = occupancy.infants
          data.children_ages = occupancy.childrenAge
          data.guest_first_name = primaryPassenger?.firstName ?? item.reservation.guestFirstName
          data.guest_last_name = primaryPassenger?.lastName ?? item.reservation.guestLastName
          data.guest_special_requests = item.reservation.guestSpecialRequests
          data.rate_plan_id = pkg.roomRate?.id
          if (OFFER_DYNAMIC_PRICING_TYPES.includes(offer.type)) {
            data.room_rate_amount = item.price
          }
        }
      }
      break
    case OFFER_TYPE_TOUR:
      data.number_of_days = pkg.duration
      if (item.reservation && startDate && endDate) {
        data.start_date = moment.utc(startDate).format(ISO_DATE_FORMAT)
        data.end_date = moment.utc(endDate).format(ISO_DATE_FORMAT)
        data.reservation_type = RESERVATION_TYPE_INSTANT_BOOKING
        data.guest_first_name = passengerDetailsWhenFlightsIncluded ? passengerDetailsWhenFlightsIncluded.firstName : item.reservation.guestFirstName
        data.guest_last_name = passengerDetailsWhenFlightsIncluded ? passengerDetailsWhenFlightsIncluded.lastName : item.reservation.guestLastName
        data.guest_special_requests = item.reservation.guestSpecialRequests
        data.dob = passengerDetailsWhenFlightsIncluded ? moment.utc(passengerDetailsWhenFlightsIncluded.birthDate).format(ISO_DATE_FORMAT) : moment.utc(item.reservation.dob).format(ISO_DATE_FORMAT)
        data.postcode = passengerDetailsWhenFlightsIncluded ? passengerDetailsWhenFlightsIncluded.postCode : item.reservation.postcode
      }
      break
  }
  return data
}

interface OrderCartExperienceItem {
  type: 'experience' | 'experience_reservation';
  id_experience_items?: string;
  brand: string;
  transaction_key: string;
  total: number;
  title: string;
  provider_offer_id: string;
  language?: string;
  le_exclusive: boolean;
  pickup_point_name?: string;
  pickup_point_id?: string;
  redemption_location_name?: string;
  redemption_location_id?: string;
  meeting_point?: string;
  cancellation_policies?: { timezoneOffset: string, refundPolicies: Array<{ id: string, type: string, value: number, periods: string[], periodLabel: string }> };
  discount_amount?: number;
  discount_percent?: number;
  booking_start_date?: string;
  expiration_date?: string;
  traveller_info: { customer: { [key: string]: unknown }, participants: { [key: string]: unknown }[] }
  ticket: {
    identifier: string;
    fareType: string;
    // includes both date and time where appropriate
    date?: string;
    productId: string;
    type: string;
  };
  special_requests?: string;
  itemId?: string;
}

export function parseCartExperienceItem(
  cartItem: App.CartExperienceItem,
  ticket: App.ExperienceItemTicket,
  transactionKey: string,
  customerDetails: Record<string, string | object>,
  travellerDetails?: Array<Record<string, string | object>>,
  itemId?: string,
): OrderCartExperienceItem {
  let ticketDate = cartItem.tickets?.[0]?.ticket.date
  if (cartItem.date) {
    ticketDate = moment.utc(cartItem.date).startOf('day').format('YYYY-MM-DD[T]HH:mm:ss')

    if (cartItem.time) {
      const [hour, minute] = cartItem.time.split(':')
      ticketDate = moment.utc(cartItem.date).minute(parseInt(minute)).hour(parseInt(hour)).format('YYYY-MM-DD[T]HH:mm:ss')
    }
  }

  return {
    type: cartItem.isBookingBNBL ? ITEM_TYPE_BNBL_EXPERIENCE : ITEM_TYPE_EXPERIENCE,
    id_experience_items: itemId,
    provider_offer_id: cartItem.experience.id,
    brand: config.BRAND,
    transaction_key: transactionKey,
    total: ticket.price,
    title: cartItem.experience.name,
    language: cartItem.language?.id,
    cancellation_policies: cartItem.experience.cancellationPolicies.length > 0 ? {
      timezoneOffset: `${new Date().getTimezoneOffset()}`,
      refundPolicies: cartItem.experience.cancellationPolicies.map(policy => ({
        periods: policy.periods,
        type: policy.type,
        value: policy.value,
        id: policy.id,
        periodLabel: policy.periodLabel,
      })),
    } : undefined,
    le_exclusive: cartItem.experience.leExclusive,
    redemption_location_id: cartItem.redemptionLocation?.id,
    redemption_location_name: cartItem.redemptionLocation?.name,
    pickup_point_id: cartItem.pickupPoint?.id,
    pickup_point_name: cartItem.pickupPoint?.name,
    traveller_info: { customer: customerDetails, participants: travellerDetails },
    ticket: {
      fareType: ticket.name,
      identifier: ticket.id,
      productId: ticket.productId,
      date: ticketDate,
      type: ticket.type,
    },
    special_requests: ticket.specialRequests,
  }
}

export function parseCartTransferItem(
  transfer: App.ExperienceTransferView,
  experience: App.ExperienceOffer,
  transactionKey: string,
) {
  const [hour, minute] = transfer.time?.split(':') ?? ['00', '00']
  const fullDateTime = moment.utc(transfer.date).minute(parseInt(minute)).hour(parseInt(hour)).format('YYYY-MM-DD[T]HH:mm:ss')

  return {
    type: ITEM_TYPE_EXPERIENCE,
    provider_offer_id: experience.id,
    brand: config.BRAND,
    transaction_key: transactionKey,
    total: transfer.option.price,
    taxes_and_fees: transfer.option.taxesAndFees,
    tickted: experience.ticketed,
    title: experience.name,
    cancellation_policies: experience.cancellationPolicies.length > 0 ? {
      timezoneOffset: `${new Date().getTimezoneOffset()}`,
      refundPolicies: experience.cancellationPolicies.map(policy => ({
        id: policy.id,
        periodLabel: policy.periodLabel,
        periods: policy.periods,
        type: policy.type,
        value: policy.value,
      })),
    } : undefined,
    le_exclusive: experience.leExclusive,
    special_requests: transfer.specialRequests,
    pickup_point_name: transfer.type === 'HOTEL-TO-AIRPORT' ? transfer.hotel.name : experience.airport.name,
    pickup_point_id: transfer.type === 'HOTEL-TO-AIRPORT' ? transfer.hotel.googlePlaceId : experience.airport.code,
    dropoff_point_name: transfer.type === 'HOTEL-TO-AIRPORT' ? experience.airport.name : transfer.hotel.name,
    dropoff_point_id: transfer.type === 'HOTEL-TO-AIRPORT' ? experience.airport.code : transfer.hotel.googlePlaceId,
    deal_options: {
      adults: transfer.travellers.adults,
      children: transfer.travellers.children ?? 0,
      child_seats: transfer.travellers.childSeats ?? 0,
      booster_seats: transfer.travellers.boosterSeats ?? 0,
      option: transfer.option,
      flight_number: transfer.flightNumber,
      type: transfer.type,
    },
    ticket: {
      date: fullDateTime,
      // time is optional so we need separate fields to know whether it was set or not
      day: transfer.date,
      time: transfer.time,
      fareType: transfer.option.name,
      identifier: transfer.option.id,
      productId: transfer.option.id,
      type: transfer.type,
    },
  }
}
